'use client'

// Starts the next Benchmark paper. A Benchmark is used up the moment it starts
// (once per paper, per account — #2161), so Start asks once more before it
// spends the paper. The server picks the paper; nothing about it is sent from
// here (the page used to leak the Benchmark form's id — practice-pool.ts).

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'

export default function StartBenchmarkButton({ label }: { label: string }) {
  const router = useRouter()
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function start() {
    setBusy(true)
    setError(null)
    try {
      const r = await fetch('/api/sat/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'benchmark' }),
      })
      const data = await r.json().catch(() => ({}))
      // An open test resumes rather than starting another (start route), and a
      // sitting already under way says so with a goto.
      if (r.ok || data?.goto) {
        router.push(data?.goto ?? '/sat/resume')
        return
      }
      setError(
        typeof data?.message === 'string'
          ? data.message
          : typeof data?.error === 'string' && data.error.includes(' ')
            ? data.error
            : "Couldn't start the Benchmark. Please try again.",
      )
      setConfirming(false)
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (!confirming) {
    return (
      <div className="flex flex-col items-end">
        <Button onClick={() => setConfirming(true)} className="min-h-[44px]">
          Start {label}
        </Button>
        {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    )
  }
  return (
    <div className="flex flex-col items-end gap-2" data-testid="benchmark-confirm">
      <p className="max-w-xs text-right text-sm">
        You can sit {label} once. It counts as soon as it starts, even if you discard it.
      </p>
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy}>
          Not now
        </Button>
        <Button onClick={start} disabled={busy} className="min-h-[44px]">
          {busy ? 'Starting…' : `Start ${label} now`}
        </Button>
      </div>
    </div>
  )
}
